import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import { SECTORS, CALENDLY_URL } from "@/content";

export default function Sectors() {
  return (
    <div data-testid="sectors-page">
      <SEO
        title="Sectors | Commercial Intelligence for Energy & Technical Markets | Thrive Business Development"
        description="Commercial market intelligence across oil & gas, renewables, offshore wind, hydrogen, power & utilities, marine, chemicals and technical engineering services."
      />

      <section className="relative overflow-hidden pt-40 pb-20 sm:pt-48 sm:pb-24" data-testid="sectors-hero">
        <div className="absolute inset-0 dot-grid-bg opacity-50" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="eyebrow mb-6">Sectors</p>
            <h1 className="font-display max-w-3xl text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-[-0.035em] leading-[1.02] text-white">
              Commercial Intelligence for Complex Markets
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-8 max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed">
              Technical B2B markets can be difficult to navigate. Buying decisions involve multiple
              stakeholders, long sales cycles and constantly changing project activity.
            </p>
            <p className="mt-4 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed">
              Thrive helps businesses understand where they fit and where commercial opportunities
              are most likely to emerge.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24" data-testid="sectors-list">
        <div className="border-t border-white/10">
          {SECTORS.map((s, i) => {
            const id = s.name.toLowerCase().replace(/[^a-z]+/g, "-");
            return (
              <Reveal key={s.name} delay={0.03}>
                <div
                  id={id}
                  data-testid={`sector-${id}`}
                  className="group grid scroll-mt-28 gap-4 border-b border-white/10 py-10 transition-colors duration-300 hover:bg-white/[0.02] sm:grid-cols-[80px_260px_1fr] sm:gap-10 sm:px-4 lg:py-12"
                >
                  <span className="font-mono text-sm text-gold/60">{String(i + 1).padStart(2, "0")}</span>
                  <h2 className="font-display text-xl sm:text-2xl font-medium tracking-[-0.03em] text-white transition-colors duration-300 group-hover:text-gold">
                    {s.name}
                  </h2>
                  <p className="max-w-2xl text-sm sm:text-base text-slate-400 leading-relaxed">{s.blurb}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="border-t border-white/10 py-20 lg:py-28" data-testid="sectors-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
          <Reveal>
            <h2 className="font-display max-w-xl text-2xl sm:text-3xl font-bold tracking-[-0.03em] text-white">
              Operating in a complex market? Let's map it properly.
            </h2>
            <p className="mt-4 max-w-xl text-base text-slate-400 leading-relaxed">
              Tell us where you want to grow and we will show you what a focused view of that market
              looks like.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sectors-book-consultation-button"
              className="group inline-flex items-center gap-2 bg-gold px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-navy transition-all duration-300 hover:bg-gold-glow"
            >
              Book a Consultation
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
